import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { localStorageService } from 'src/common/service/local-storage-service'

export const HOME_APP_TOUR_ONBOARDING_STEP = {
  PICK_USERNAME: 'pick_username',
} as const

export type HomeAppTourOnboardingStep = (typeof HOME_APP_TOUR_ONBOARDING_STEP)[keyof typeof HOME_APP_TOUR_ONBOARDING_STEP]

const REQUIRED_STEPS_FOR_HOME_APP_TOUR: readonly HomeAppTourOnboardingStep[] = [HOME_APP_TOUR_ONBOARDING_STEP.PICK_USERNAME]

function readStepFromStorage(step: HomeAppTourOnboardingStep): boolean {
  switch (step) {
    case HOME_APP_TOUR_ONBOARDING_STEP.PICK_USERNAME:
      return localStorageService.getPickUsernameModalSeen()
    default: {
      const _exhaustive: never = step
      return _exhaustive
    }
  }
}

type CompletionMap = Partial<Record<HomeAppTourOnboardingStep, boolean>>

function allRequiredComplete(m: CompletionMap): boolean {
  return REQUIRED_STEPS_FOR_HOME_APP_TOUR.every((step) => m[step] === true)
}

export type HomeOnboardingAppTourGateValue = {
  canStartHomeAppTour: boolean
  notifyStepComplete: (step: HomeAppTourOnboardingStep) => void
}

const HomeOnboardingAppTourGateContext = createContext<HomeOnboardingAppTourGateValue | null>(null)

function initialCompletion(): CompletionMap {
  const next: CompletionMap = {}
  for (const step of REQUIRED_STEPS_FOR_HOME_APP_TOUR) {
    next[step] = readStepFromStorage(step)
  }
  return next
}

export function HomeOnboardingAppTourGateProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [completion, setCompletion] = useState<CompletionMap>(() => initialCompletion())

  const notifyStepComplete = useCallback((step: HomeAppTourOnboardingStep): void => {
    setCompletion((prev) => ({ ...prev, [step]: true }))
  }, [])

  const canStartHomeAppTour = allRequiredComplete(completion)

  const value = useMemo(() => ({ canStartHomeAppTour, notifyStepComplete }), [canStartHomeAppTour, notifyStepComplete])

  return <HomeOnboardingAppTourGateContext.Provider value={value}>{children}</HomeOnboardingAppTourGateContext.Provider>
}

export function useHomeOnboardingAppTourGate(): HomeOnboardingAppTourGateValue {
  const ctx = useContext(HomeOnboardingAppTourGateContext)
  if (!ctx) {
    throw new Error('useHomeOnboardingAppTourGate must be used within HomeOnboardingAppTourGateProvider')
  }
  return ctx
}
